"use client";

import React from "react";
import { NavigationProvider } from "@/lib/navigation/NavigationProvider";
import { useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { PopularMatchesTicker } from "./components/PopularMatchesTicker";
import { LiveScoreWidget } from "./components/LiveScoreWidget";
import { HomeLivescoreProvider } from "./components/HomeLivescoreProvider";
import { NewsBlogCompetitionSection } from "./components/NewsBlogCompetitionSection";
import { FeaturedPostsCarousel } from "@/views/home/parts/FeaturedPostsCarousel";
import { CategorySection } from "@/views/home/parts/CategorySection";
import { PromoSection } from "@/views/home/parts/PromoSection";
import { SectionStrip } from "@/components/layout/SectionStrip";
import type { BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_MATCHES, HOME_COMPETITIONS } from "@/features/theme/sectionKeys";
import type { NewsBlogHomeClassicData } from "./types";
import { useNavigation } from "@/lib/navigation/NavigationProvider";

export interface NewsBlogHomeClassicLayoutProps {
    data: NewsBlogHomeClassicData;
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

/**
 * NewsBlogHomeClassicLayout - Client layout for news-blog home classic variant.
 *
 * Layout sections (from top to bottom):
 * 1. Breaking News Ticker (placeholder)
 * 2. Hero news section - latest articles (FeaturedContentBlock with Spotlight variant)
 * 3. Live Score Widget (placeholder)
 * 4. Competition sections - one per competition in tenant config
 *
 * Each competition section is wrapped in NavigationProvider to provide
 * the competition ID context for StandingsCompact and MatchTabbedList.
 */
export function NewsBlogHomeClassicLayout({
    data,
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles,
}: NewsBlogHomeClassicLayoutProps) {
    const nav = useNavigation();
    const isLivescoreEnabled = useFeatureFlag("livescore");

    const content = (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* Popular Matches Ticker */}
                {isLivescoreEnabled && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                        <PopularMatchesTicker popularCompetitionIds={data.popularCompetitionIds} ticker={data.ticker} />
                    </SectionStrip>
                )}

                {/* Hero News Section - Spotlight variant with main featured item and supporting items */}
                {(data.featuredPosts.length > 0 || data.latestNews.length > 0) && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <FeaturedPostsCarousel
                            featuredPosts={data.featuredPosts}
                            latestPosts={data.latestNews}
                            priority
                        />
                    </SectionStrip>
                )}

                {/* Promo slot 0 */}
                <PromoSlot row={data.promos[0] ?? null} />

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Live Score Widget */}
                {isLivescoreEnabled && (
                    <LiveScoreWidget
                        popularCompetitionIds={data.popularCompetitionIds}
                        livescoreUrl={nav.livescore()}
                    />
                )}

                {/* Video Shorts */}
                {videoShortsSlot}

                {/* Content stream — competition / category / promo sections */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_COMPETITIONS]}>
                    {data.sections.map((section, i) => {
                        if (section.kind === "competition") {
                            return (
                                <NavigationProvider
                                    key={`comp-${section.data.nativeId}-${i}`}
                                    competitionId={section.data.nativeId}
                                >
                                    <NewsBlogCompetitionSection data={section.data} />
                                </NavigationProvider>
                            );
                        }
                        if (section.kind === "category") {
                            return (
                                <CategorySection
                                    key={`cat-${section.data.categoryId}-${i}`}
                                    data={section.data}
                                />
                            );
                        }
                        if (section.kind === "promo") {
                            return <PromoSection key={`promo-${i}`} promo={section.data} />;
                        }
                        return null;
                    })}
                </SectionStrip>

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Promo slot 1 */}
                <PromoSlot row={data.promos[1] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );

    if (isLivescoreEnabled) {
        return (
            <HomeLivescoreProvider locale={data.locale} initialBoard={data.livescoreBoard}>
                {content}
            </HomeLivescoreProvider>
        );
    }
    return content;
}
